/**
 * SetupWizard Engine — Motor puro de navegación para wizards paso a paso.
 *
 * CERO conocimiento de negocio. Solo sabe:
 * - Renderizar el paso actual
 * - Validar antes de avanzar
 * - Mostrar resumen en el último paso
 * - Llamar onConfirm al final
 *
 * Los pasos se inyectan como array de objetos con interfaz:
 *   { id, label, subtitle, render(container, data), validate(data), collect(data), summarize(data) }
 */

import { App } from '../admin.js';
import { t } from '../services/i18n-admin.js';

const WIZARD_MODAL_ID = 'modalSetupWizard';

export const SetupWizard = {
    // --- Estado ---
    _steps: [],
    _currentStepIndex: 0,
    _data: {},          // Acumulado por collect() de cada paso
    _onConfirm: null,   // async (data) => result
    _onComplete: null,   // (result) => void  — callback post-creación

    /**
     * Abre el wizard con pasos inyectados.
     * @param {Object} opts
     * @param {string}   opts.title       Título del modal
     * @param {Array}    opts.steps       Array de step definitions
     * @param {Object}   opts.initialData Datos iniciales (código, parentId, etc.)
     * @param {Function} opts.onConfirm   async (data) => result — llamada al API
     * @param {Function} opts.onComplete  (result) => void — post-creación (cerrar, recargar, etc.)
     */
    open({ title, steps, initialData = {}, onConfirm, onComplete }) {
        this._steps = steps;
        this._currentStepIndex = 0;
        this._data = { ...initialData };
        this._onConfirm = onConfirm;
        this._onComplete = onComplete;

        App.openModal(WIZARD_MODAL_ID);

        document.getElementById('wizardTitle').textContent = title;

        this._renderTabs();
        this._renderStep();
        this._updateFooter();
    },

    // --- Navegación ---

    next() {
        const step = this._steps[this._currentStepIndex];

        // Recolectar datos del paso actual
        if (step.collect) {
            step.collect(this._data);
        }

        // Validar
        if (step.validate) {
            const result = step.validate(this._data);
            if (result && result.error) {
                App.toast(result.error, 'error');
                return;
            }
        }

        if (this._currentStepIndex < this._steps.length - 1) {
            // Pasos condicionales: step.skip(data) === true → el paso no aplica a
            // este contexto y se salta (ej: "Jogos" en un POS solo-wallet). El
            // wizard pregunta lo justo: ni de más ni de menos (dueño 2026-06-12).
            let destino = this._currentStepIndex + 1;
            while (destino < this._steps.length - 1 && this._steps[destino].skip?.(this._data)) {
                destino++;
            }
            this._currentStepIndex = destino;
            this._renderTabs();
            this._renderStep();
            this._updateFooter();
        }
    },

    prev() {
        // Recolectar datos antes de retroceder (preservar lo escrito)
        const step = this._steps[this._currentStepIndex];
        if (step.collect) {
            step.collect(this._data);
        }

        if (this._currentStepIndex > 0) {
            let destino = this._currentStepIndex - 1;
            while (destino > 0 && this._steps[destino].skip?.(this._data)) {
                destino--;
            }
            this._currentStepIndex = destino;
            this._renderTabs();
            this._renderStep();
            this._updateFooter();
        }
    },

    async confirm() {
        if (!this._onConfirm) return;

        const confirmButton = document.getElementById('wizardBtnNext');
        const originalText = confirmButton.textContent;
        confirmButton.disabled = true;
        confirmButton.textContent = t('Guardando...');

        try {
            const result = await this._onConfirm(this._data);
            App.closeModal();

            if (this._onComplete) {
                this._onComplete(result);
            }
        } catch (err) {
            App.toast(err.message || t('Error al guardar'), 'error');
        } finally {
            confirmButton.disabled = false;
            confirmButton.textContent = originalText;
        }
    },

    // --- Render privado ---

    _renderTabs() {
        const tabsContainer = document.getElementById('wizardTabs');
        let html = '';

        let numeroVisible = 0;
        for (let i = 0; i < this._steps.length; i++) {
            const step = this._steps[i];
            // Paso que no aplica a este contexto: no se muestra en el stepper.
            if (step.skip?.(this._data)) continue;
            numeroVisible++;
            let stateClass = 'wizard-step-locked';
            let icon = `<span class="wizard-step-number">${numeroVisible}</span>`;

            if (i < this._currentStepIndex) {
                stateClass = 'wizard-step-completed';
                icon = '<span class="wizard-step-check">✓</span>';
            } else if (i === this._currentStepIndex) {
                stateClass = 'wizard-step-current';
                icon = `<span class="wizard-step-number current">${numeroVisible}</span>`;
            }

            html += `
                <div class="modal-tab ${stateClass}" data-step="${i}"
                     ${i <= this._currentStepIndex ? `data-action="wizard:goToStep" data-param="${i}"` : ''}>
                    ${icon}
                    <div class="tab-text">
                        <span class="tab-title">${step.label}</span>
                        ${step.subtitle ? `<span class="tab-desc">${step.subtitle}</span>` : ''}
                    </div>
                </div>`;
        }

        tabsContainer.innerHTML = html;
    },

    _renderStep() {
        const content = document.getElementById('wizardContent');
        const step = this._steps[this._currentStepIndex];
        const isLastStep = this._currentStepIndex === this._steps.length - 1;

        content.innerHTML = '';

        if (isLastStep && step.id === '_resumen') {
            // Paso resumen auto-generado: pedir summarize() a todos los pasos anteriores
            this._renderSummary(content);
        } else {
            step.render(content, this._data);
        }
    },

    _renderSummary(container) {
        let html = '';

        for (let i = 0; i < this._steps.length - 1; i++) {
            const step = this._steps[i];
            if (step.skip?.(this._data)) continue; // paso no aplicado: nada que resumir
            if (step.summarize) {
                const summaryHtml = step.summarize(this._data);
                if (summaryHtml) {
                    html += `
                        <div class="wizard-summary-section">
                            <div class="wizard-summary-header">${step.label}</div>
                            ${summaryHtml}
                        </div>`;
                }
            }
        }

        container.innerHTML = html || `<p style="color:var(--text-secondary);">${t('Sin datos para mostrar')}</p>`;
    },

    _updateFooter() {
        const btnBack = document.getElementById('wizardBtnBack');
        const btnNext = document.getElementById('wizardBtnNext');
        const isFirst = this._currentStepIndex === 0;
        const isLast = this._currentStepIndex === this._steps.length - 1;

        btnBack.style.display = isFirst ? 'none' : 'inline-flex';

        if (isLast) {
            btnNext.textContent = t('Confirmar');
            btnNext.dataset.action = 'wizard:confirm';
            btnNext.className = 'btn btn-primary';
        } else {
            btnNext.textContent = t('Siguiente →');
            btnNext.dataset.action = 'wizard:next';
            btnNext.className = 'btn btn-primary';
        }
    },

    // Navegar a un paso ya visitado (click en tab completado)
    goToStep(stepIndex) {
        const idx = Number(stepIndex);
        if (idx > this._currentStepIndex) return; // no saltar adelante

        // Recolectar datos del paso actual antes de saltar
        const step = this._steps[this._currentStepIndex];
        if (step.collect) {
            step.collect(this._data);
        }

        this._currentStepIndex = idx;
        this._renderTabs();
        this._renderStep();
        this._updateFooter();
    },

    /**
     * Helper para crear el paso Resumen estándar.
     * Se agrega automáticamente como último paso.
     */
    createSummaryStep() {
        return {
            id: '_resumen',
            label: t('Resumen'),
            subtitle: t('Confirmar datos'),
            render: (container, data) => this._renderSummary(container),
            validate: null,
            collect: null,
            summarize: null,
        };
    },
};
